import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api from '../api';
import PageHeader from './PageHeader';

export default function AudioMemory({ onSuccess, onBack }) {
  const [guestName, setGuestName] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [waveformHeights, setWaveformHeights] = useState(Array(24).fill(8));

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const streamRef = useRef(null);

  const updateWaveform = useCallback(() => {
    if (!analyserRef.current) return;
    const data = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(data);
    const step = Math.floor(data.length / 24);
    const heights = [];
    for (let i = 0; i < 24; i++) {
      const val = data[i * step] || 0;
      heights.push(Math.max(4, (val / 255) * 48));
    }
    setWaveformHeights(heights);
    animFrameRef.current = requestAnimationFrame(updateWaveform);
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Waveform için AudioContext
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        cancelAnimationFrame(animFrameRef.current);
        setWaveformHeights(Array(24).fill(8));
      };

      recorder.start(100);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);

      updateWaveform();
    } catch {
      alert('Mikrofon erişimi reddedildi. Lütfen tarayıcı ayarlarından izin verin.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setDuration(0);
  };

  const handleSubmit = async () => {
    if (!guestName.trim() || !audioBlob) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('guestName', guestName.trim());
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('screenSize', window.screen.width + 'x' + window.screen.height);
      formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
      formData.append('platform', navigator.platform);
      await api.post('/api/memories/audio', formData);
      onSuccess('Sesli anınız kaydedildi! Teşekkür ederiz 🎙️');
      setGuestName('');
      setAudioBlob(null);
      setDuration(0);
    } catch {
      alert('Bir hata oluştu, lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      cancelAnimationFrame(animFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex flex-col items-center px-6 pt-14 pb-8 max-w-md mx-auto">
      <PageHeader title="Sesli Anı Defteri" onBack={onBack} />

      {/* Başlık */}
      <div className="text-center mb-8 mt-4">
        <h2 className="font-playfair text-2xl text-sage-800 font-semibold">Sesli Anı Defteri</h2>
        <p className="text-sage-500 text-sm mt-2">Sesinizle güzel bir anı bırakın</p>
      </div>

      {/* İsim girişi */}
      <div className="w-full mb-6">
        <label className="block text-sage-600 text-sm font-medium mb-2">Adınız</label>
        <input
          type="text"
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="Adınız ve soyadınız"
          maxLength={100}
          className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-sage-200 rounded-xl
                     text-sage-800 placeholder:text-sage-300 focus:outline-none focus:border-gold-400
                     focus:ring-2 focus:ring-gold-300/20 transition-all"
        />
      </div>

      {/* Waveform / Kayıt alanı */}
      <motion.div
        layout
        className="w-full bg-white/70 backdrop-blur-sm border-2 border-sage-200 rounded-2xl p-6 mb-6
                   flex flex-col items-center justify-center min-h-[200px]"
        style={isRecording ? { borderColor: '#c9a83e' } : {}}
      >
        {/* Waveform görseli */}
        <div className="flex items-end justify-center gap-1 h-16 mb-4">
          {waveformHeights.map((h, i) => (
            <motion.div
              key={i}
              animate={{ height: h }}
              transition={{ duration: 0.1 }}
              className={`w-1.5 rounded-full ${isRecording ? 'bg-gold-500' : 'bg-sage-200'}`}
            />
          ))}
        </div>

        {/* Zamanlayıcı */}
        <p className={`font-montserrat text-2xl font-light mb-4 ${isRecording ? 'text-gold-600' : 'text-sage-400'}`}>
          {formatTime(duration)}
        </p>

        {/* Kontrol butonları */}
        <div className="flex items-center gap-4">
          {!isRecording && !audioBlob && (
            <button
              onClick={startRecording}
              className="w-16 h-16 bg-sage-600 hover:bg-sage-700 rounded-full flex items-center justify-center
                         text-white shadow-lg shadow-sage-300/40 transition-all active:scale-95"
            >
              <div className="w-5 h-5 bg-red-400 rounded-full" />
            </button>
          )}

          {isRecording && (
            <button
              onClick={stopRecording}
              className="w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center
                         text-white shadow-lg shadow-red-300/40 transition-all active:scale-95 gold-shimmer border-2"
            >
              <div className="w-5 h-5 bg-white rounded-sm" />
            </button>
          )}

          {audioBlob && (
            <>
              <button
                onClick={resetRecording}
                className="px-5 py-2.5 bg-sage-100 text-sage-600 rounded-xl text-sm font-medium
                           hover:bg-sage-200 transition-all"
              >
                Tekrar Kaydet
              </button>
              <button
                onClick={handleSubmit}
                disabled={!guestName.trim() || submitting}
                className="px-5 py-2.5 bg-sage-600 text-white rounded-xl text-sm font-medium
                           hover:bg-sage-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </>
          )}
        </div>

        {isRecording && (
          <p className="text-red-400 text-xs mt-3 animate-pulse">Kayıt yapılıyor...</p>
        )}
      </motion.div>

      {/* Bilgi notu */}
      <p className="text-sage-400 text-xs text-center">
        Kayıt butonuna basarak sesinizi kaydedebilirsiniz.<br />
        Kayıt, çiftimizin anı defterinde saklanacaktır.
      </p>
    </div>
  );
}
